/**
 * Regional Settings API utilities
 * Fetch data from master tables for dropdowns
 *
 * The fallback lists below fire ONLY on a failed request. They did not save
 * the Regional tab when the master tables were empty, because an empty table
 * answers `200 []` — a success, not an error — and an empty dropdown looks
 * exactly like a working one. The tables are seeded by
 * `enterprise_platform/regional/migrations/0002_seed_regional_master_data.py`;
 * keep the codes below identical to the codes seeded there, so a value a lab
 * has already saved still matches an option when a request does fail.
 */

import { authenticatedApi } from './authenticatedApi';

const API_BASE = '/v1/platform/regional';

export interface TimezoneOption {
  id: string;
  timezone_id: string;
  display_name: string;
  region: string;
  is_commonly_used: boolean;
  usage_priority: number;
}

export interface LanguageOption {
  id: string;
  language_code: string;
  english_name: string;
  native_name?: string;
  region: string;
  is_commonly_used: boolean;
  usage_priority: number;
}

export interface DateFormatOption {
  id: string;
  format_code: string;
  display_name: string;
  example: string;
  is_commonly_used: boolean;
  usage_priority: number;
}

export interface TimeFormatOption {
  id: string;
  format_code: string;
  display_name: string;
  example: string;
  hour_format: '12' | '24';
  is_commonly_used: boolean;
  usage_priority: number;
}

/**
 * Fetch common timezones for dropdown
 */
export const fetchCommonTimezones = async (): Promise<TimezoneOption[]> => {
  try {
    console.log('🌍 Fetching timezones from:', `${API_BASE}/timezones/common/`);
    const response = await authenticatedApi.get(`${API_BASE}/timezones/common/`);
    
    console.log('🌍 Timezone response status:', response.status);
    
    if (response.error) {
      console.error('🌍 Timezone API error response:', response.error);
      throw new Error(`API error: ${response.error}`);
    }
    
    const data = response.data;
    console.log('🌍 Timezone data received:', data?.value?.length || data?.length, 'items');
    console.log('🌍 First 3 timezones:', data?.slice?.(0, 3) || data?.value?.slice?.(0, 3));
    // Handle both wrapped and direct array responses
    return data?.value || data;
  } catch (error) {
    console.error('🌍 Failed to fetch timezones:', error);
    console.log('🌍 Using fallback timezone data');
    // Fallback to basic options if API fails
    return [
      { id: '1', timezone_id: 'Indian/Mauritius', display_name: 'Mauritius (UTC+04:00)', region: 'Indian Ocean', is_commonly_used: true, usage_priority: 1 },
      { id: '2', timezone_id: 'UTC', display_name: 'UTC — Coordinated Universal Time', region: 'Global', is_commonly_used: true, usage_priority: 2 },
      { id: '3', timezone_id: 'Africa/Johannesburg', display_name: 'Johannesburg (UTC+02:00)', region: 'Africa', is_commonly_used: true, usage_priority: 3 },
      { id: '4', timezone_id: 'Europe/London', display_name: 'London (UTC+00:00)', region: 'Europe', is_commonly_used: true, usage_priority: 4 },
      { id: '5', timezone_id: 'Europe/Paris', display_name: 'Paris (UTC+01:00)', region: 'Europe', is_commonly_used: true, usage_priority: 5 },
      { id: '6', timezone_id: 'Asia/Dubai', display_name: 'Dubai (UTC+04:00)', region: 'Middle East', is_commonly_used: true, usage_priority: 6 },
      { id: '7', timezone_id: 'Asia/Kolkata', display_name: 'India (Kolkata) (UTC+05:30)', region: 'Asia', is_commonly_used: true, usage_priority: 7 },
      { id: '8', timezone_id: 'America/New_York', display_name: 'New York (Eastern) (UTC-05:00)', region: 'Americas', is_commonly_used: true, usage_priority: 8 },
    ];
  }
};

/**
 * Fetch common languages for dropdown
 */
export const fetchCommonLanguages = async (): Promise<LanguageOption[]> => {
  try {
    console.log('🗣️ Fetching languages from:', `${API_BASE}/languages/common/`);
    const response = await authenticatedApi.get(`${API_BASE}/languages/common/`);
    
    console.log('🗣️ Language response status:', response.status);
    
    if (response.error) {
      console.error('🗣️ Language API error response:', response.error);
      throw new Error(`API error: ${response.error}`);
    }
    
    const data = response.data;
    console.log('🗣️ Language data received:', data?.value?.length || data?.length, 'items');
    console.log('🗣️ First 3 languages:', data?.slice?.(0, 3) || data?.value?.slice?.(0, 3));
    // Handle both wrapped and direct array responses
    return data?.value || data;
  } catch (error) {
    console.error('🗣️ Failed to fetch languages:', error);
    console.log('🗣️ Using fallback language data');
    // Fallback to basic options if API fails
    return [
      { id: '1', language_code: 'en', english_name: 'English', native_name: 'English', region: 'Global', is_commonly_used: true, usage_priority: 1 },
      { id: '2', language_code: 'fr', english_name: 'French', native_name: 'Français', region: 'Europe', is_commonly_used: true, usage_priority: 2 },
      { id: '3', language_code: 'mfe', english_name: 'Mauritian Creole', native_name: 'Kreol Morisien', region: 'Indian Ocean', is_commonly_used: true, usage_priority: 3 },
      { id: '4', language_code: 'es', english_name: 'Spanish', native_name: 'Español', region: 'Europe', is_commonly_used: true, usage_priority: 4 },
      { id: '5', language_code: 'de', english_name: 'German', native_name: 'Deutsch', region: 'Europe', is_commonly_used: true, usage_priority: 5 },
      { id: '6', language_code: 'zh', english_name: 'Chinese (Simplified)', native_name: '简体中文', region: 'Asia', is_commonly_used: true, usage_priority: 6 },
    ];
  }
};

/**
 * Fetch common date formats for dropdown
 */
export const fetchCommonDateFormats = async (): Promise<DateFormatOption[]> => {
  try {
    console.log('📅 Fetching date formats from:', `${API_BASE}/date-formats/common/`);
    const response = await authenticatedApi.get(`${API_BASE}/date-formats/common/`);
    
    console.log('📅 Date format response status:', response.status);
    
    if (response.error) {
      console.error('📅 Date format API error response:', response.error);
      throw new Error(`API error: ${response.error}`);
    }
    
    const data = response.data;
    console.log('📅 Date format data received:', data?.value?.length || data?.length, 'items');
    console.log('📅 First 3 date formats:', data?.slice?.(0, 3) || data?.value?.slice?.(0, 3));
    // Handle both wrapped and direct array responses
    return data?.value || data;
  } catch (error) {
    console.error('📅 Failed to fetch date formats:', error);
    console.log('📅 Using fallback date format data');
    // Fallback to basic options if API fails
    return [
      { id: '1', format_code: 'DD/MM/YYYY', display_name: 'Day / Month / Year', example: '15/03/2026', is_commonly_used: true, usage_priority: 1 },
      { id: '2', format_code: 'YYYY-MM-DD', display_name: 'ISO 8601', example: '2026-03-15', is_commonly_used: true, usage_priority: 2 },
      { id: '3', format_code: 'DD-MM-YYYY', display_name: 'Day - Month - Year', example: '15-03-2026', is_commonly_used: true, usage_priority: 3 },
      { id: '4', format_code: 'DD.MM.YYYY', display_name: 'Day . Month . Year', example: '15.03.2026', is_commonly_used: true, usage_priority: 4 },
      { id: '5', format_code: 'DD MMM YYYY', display_name: 'Day Month-name Year', example: '15 Mar 2026', is_commonly_used: true, usage_priority: 5 },
      { id: '6', format_code: 'MM/DD/YYYY', display_name: 'Month / Day / Year (US)', example: '03/15/2026', is_commonly_used: true, usage_priority: 6 },
    ];
  }
};

/**
 * Fetch common time formats for dropdown
 */
export const fetchCommonTimeFormats = async (): Promise<TimeFormatOption[]> => {
  try {
    console.log('🕐 Fetching time formats from:', `${API_BASE}/time-formats/common/`);
    const response = await authenticatedApi.get(`${API_BASE}/time-formats/common/`);
    
    console.log('🕐 Time format response status:', response.status);
    
    if (response.error) {
      console.error('🕐 Time format API error response:', response.error);
      throw new Error(`API error: ${response.error}`);
    }
    
    const data = response.data;
    console.log('🕐 Time format data received:', data?.value?.length || data?.length, 'items');
    console.log('🕐 First 3 time formats:', data?.slice?.(0, 3) || data?.value?.slice?.(0, 3));
    // Handle both wrapped and direct array responses
    return data?.value || data;
  } catch (error) {
    console.error('🕐 Failed to fetch time formats:', error);
    console.log('🕐 Using fallback time format data');
    // Fallback to basic options if API fails
    return [
      { id: '1', format_code: 'HH:mm', display_name: '24-hour', example: '14:30', hour_format: '24', is_commonly_used: true, usage_priority: 1 },
      { id: '2', format_code: 'hh:mm A', display_name: '12-hour (AM/PM)', example: '02:30 PM', hour_format: '12', is_commonly_used: true, usage_priority: 2 },
      { id: '3', format_code: 'HH:mm:ss', display_name: '24-hour with seconds', example: '14:30:45', hour_format: '24', is_commonly_used: true, usage_priority: 3 },
      { id: '4', format_code: 'hh:mm:ss A', display_name: '12-hour with seconds', example: '02:30:45 PM', hour_format: '12', is_commonly_used: true, usage_priority: 4 },
    ];
  }
};